import { useEffect } from 'react';
import { TreeViewPageShell } from '@elementary/common-ui/components';
import { Report as ReportType } from '@elementary/common-ui/types';
import { Navigate, Route, Routes } from 'react-router-dom';
import useEnvPermission from '@elementary/common-ui/hooks/useEnvPermission';
import { isInSaaSMode } from '@elementary/common-ui/utils/common';
import Dashboard from '@/dashboard';
import Lineage from '@/lineage';
import ModelRuns from '@/modelRuns';
import useReport from '@/shared/hooks/useReport';
import TestResults from '@/testResults';
import TestRuns from '@/testRuns';
import ReportEmptyStatePage from './ReportEmptyStatePage';
import ReportShell from './ReportShell';
import ErrorPage from './ErrorPage';

type Props = {
  reportDataOverride?: ReportType;
  renderEmptyStatePageOverride?: () => JSX.Element;
};

const Report = ({
  reportDataOverride,
  renderEmptyStatePageOverride,
}: Props) => {
  const { initReport, reportData } = useReport();
  const hasPermission = useEnvPermission();

  useEffect(() => {
    initReport(reportDataOverride);
  }, [initReport, reportDataOverride]);

  const hasDashboardAccess =
    hasPermission('VIEW_ASSETS') && hasPermission('VIEW_TESTS');
  const defaultRedirect = hasDashboardAccess ? 'dashboard' : '/';

  return (
    <Routes>
      <Route element={<ReportShell />}>
        {hasDashboardAccess && (
          <Route path="dashboard" element={<Dashboard />} />
        )}

        <Route
          element={
            <TreeViewPageShell
              reportData={reportData}
              renderEmptyStatePage={
                renderEmptyStatePageOverride || (() => <ReportEmptyStatePage />)
              }
              renderErrorPage={({ error }) => (
                <ErrorPage error={error} page={'Report-TreeViewPageShell'} />
              )}
              useApi={isInSaaSMode()}
            />
          }
        >
          {hasPermission('VIEW_TESTS') && (
            <>
              <Route path="test-results" element={<TestResults />} />
              <Route path="test-results/:id" element={<TestResults />} />
              <Route path="test-runs" element={<TestRuns />} />
              <Route path="test-runs/:id" element={<TestRuns />} />
            </>
          )}
          {hasPermission('VIEW_ASSETS') && (
            <>
              <Route path="model-runs" element={<ModelRuns />} />
              <Route path="model-runs/:id" element={<ModelRuns />} />
              <Route path="lineage" element={<Lineage />} />
            </>
          )}
        </Route>

        <Route path="*" element={<Navigate to={defaultRedirect} />} />
      </Route>
    </Routes>
  );
};

export default Report;
